import { createFileRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Page } from '@/components/layout/page'
import { Skeleton } from '@/components/ui/skeleton'
import { api } from '@/api/http'
import type { CSSProperties } from 'react'
import type { GPHomeResponse, FranchiseItem, BucketKey } from '../../-components/types'
import { BUCKET_KEYS, LEVEL_META, isBDE, inr } from '../../-components/types'

export const Route = createFileRoute('/mrgolisoda/gp-home/bucket/$bucketKey/')({
  validateSearch: (s: Record<string, unknown>) => ({
    date: typeof s.date === 'string' ? s.date : undefined,
    employee_id: typeof s.employee_id === 'number' ? s.employee_id : undefined,
  }),
  component: BucketBreakdownPage,
})

function todayStr() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getAllFranchises(data: GPHomeResponse): FranchiseItem[] {
  if (isBDE(data)) return data.franchises
  return data.data.flatMap((e) => e.franchises)
}

// ─── Table styles ─────────────────────────────────────────────────────────────

const TH: CSSProperties = {
  padding: '4px 6px',
  fontWeight: 600,
  fontSize: 10,
  color: '#808080',
  borderBottom: '1px solid #E5E5E5',
  textAlign: 'right',
}

const TD: CSSProperties = {
  padding: '10px 6px',
  fontSize: 13,
  color: '#1A1A1A',
  fontVariantNumeric: 'tabular-nums',
  borderBottom: '1px solid #F0F0F0',
  textAlign: 'right',
}

// ─── Section table ─────────────────────────────────────────────────────────────

function FranchiseTable({
  franchises,
  bucketKey,
  section,
}: {
  franchises: FranchiseItem[]
  bucketKey: BucketKey
  section: 'incomplete' | 'completed'
}) {
  const totTarget = franchises.reduce((s, f) => s + f.buckets[bucketKey].target, 0)
  const totDone = franchises.reduce((s, f) => s + f.buckets[bucketKey].accomplished, 0)
  const totPct = totTarget > 0 ? Math.round((totDone / totTarget) * 100) : 0

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
      <thead>
        <tr>
          <th style={{ ...TH, textAlign: 'left', width: '40%' }}>Franchise</th>
          <th style={{ ...TH, width: '22%' }}>Target</th>
          <th style={{ ...TH, width: '22%' }}>Done</th>
          <th style={{ ...TH, width: '16%' }}>%</th>
        </tr>
      </thead>
      <tbody>
        {franchises.map((f) => {
          const b = f.buckets[bucketKey]
          const needs = b.target - b.accomplished
          const pctColor = section === 'incomplete' ? '#E51414' : '#226600'
          return (
            <tr key={f.yolo_customer_id}>
              <td style={{ ...TD, textAlign: 'left', overflow: 'hidden' }}>
                <div style={{ fontWeight: 600, color: '#104BFC', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{f.franchise}</div>
                {section === 'incomplete' && needs > 0 && (
                  <div style={{ fontSize: 11, color: '#808080', marginTop: 1 }}>
                    needs {inr(needs)}
                  </div>
                )}
                {section === 'completed' && b.accomplished > b.target && (
                  <div style={{ fontSize: 11, color: '#226600', marginTop: 1 }}>
                    +{inr(b.accomplished - b.target)}
                  </div>
                )}
              </td>
              <td style={{ ...TD, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{inr(b.target)}</td>
              <td style={{ ...TD, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{inr(b.accomplished)}</td>
              <td style={{ ...TD, color: pctColor, fontWeight: 700, whiteSpace: 'nowrap' }}>{b.pct}%</td>
            </tr>
          )
        })}
        <tr style={{ fontWeight: 800 }}>
          <td style={{ ...TD, textAlign: 'left', borderBottom: 'none', borderTop: '1.5px solid #E5E5E5' }}>
            Total
          </td>
          <td style={{ ...TD, borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{inr(totTarget)}</td>
          <td style={{ ...TD, borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{inr(totDone)}</td>
          <td style={{ ...TD, borderBottom: 'none', borderTop: '1.5px solid #E5E5E5', color: section === 'incomplete' ? '#E51414' : '#226600', fontWeight: 800, whiteSpace: 'nowrap' }}>
            {totPct}%
          </td>
        </tr>
      </tbody>
    </table>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function BucketBreakdownPage() {
  const { bucketKey: rawKey } = Route.useParams()
  const { date: dateParam, employee_id: employeeId } = Route.useSearch()
  const date = dateParam ?? todayStr()

  const bucketKey = BUCKET_KEYS.includes(rawKey as BucketKey) ? (rawKey as BucketKey) : null
  const meta = bucketKey ? LEVEL_META[bucketKey] : null

  const queryStr = employeeId != null
    ? `?date=${date}&employee_id=${employeeId}`
    : `?date=${date}`

  const { data, isLoading } = useQuery({
    queryKey: ['gp-home', date, employeeId],
    queryFn: () => api.get<GPHomeResponse>(`/field-customization/goli-soda/gp-home/${queryStr}`),
    enabled: !!bucketKey,
  })

  const allFranchises = data ? getAllFranchises(data) : []
  const configured = bucketKey ? allFranchises.filter((f) => f.buckets[bucketKey].target > 0) : []
  const incomplete = bucketKey ? configured.filter((f) => f.buckets[bucketKey].pct < 100) : []
  const completed = bucketKey ? configured.filter((f) => f.buckets[bucketKey].pct >= 100) : []

  if (!bucketKey || !meta) {
    return (
      <Page title="Not found" back scaled>
        <p style={{ padding: 16, fontSize: 13, color: '#808080' }}>Invalid bucket.</p>
      </Page>
    )
  }

  return (
    <Page title={`${meta.label} breakdown`} back scaled>
      <div style={{ padding: '16px 16px 40px', background: '#F5F5F5', minHeight: '100%', display: 'flex', flexDirection: 'column', gap: 12 }}>

        {isLoading && <Skeleton className="h-48 w-full rounded-xl" />}

        {!isLoading && configured.length > 0 && (
          <>
            {/* Alert banner */}
            <div style={{ background: meta.bg, borderRadius: 10, padding: '10px 14px', fontSize: 12, fontWeight: 600, color: meta.text }}>
              {completed.length} of {configured.length} crossed {meta.label}.
              {incomplete.length > 0 && ` These ${incomplete.length} have not.`}
            </div>

            {/* Incomplete section */}
            {incomplete.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#E51414', marginBottom: 10 }}>
                  Not covered ({incomplete.length})
                </p>
                <FranchiseTable franchises={incomplete} bucketKey={bucketKey} section="incomplete" />
              </div>
            )}

            {/* Completed section */}
            {completed.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 14 }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: '#226600', marginBottom: 10 }}>
                  Covered ({completed.length})
                </p>
                <FranchiseTable franchises={completed} bucketKey={bucketKey} section="completed" />
              </div>
            )}
          </>
        )}

        {!isLoading && configured.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #EBEBEB', padding: 24, textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: '#808080' }}>No franchise data for {meta.label}</p>
          </div>
        )}
      </div>
    </Page>
  )
}
